export const MIN_CONSENSUS_PROVIDERS = 4;
export const ABS_TOLERANCE_USD = 20;
export const REL_TOLERANCE = 0.002;

export type PriceQuote = { provider: string; price: number };

export function toleranceUsd(median: number): number {
	return Math.max(ABS_TOLERANCE_USD, median * REL_TOLERANCE);
}

function median(values: number[]): number {
	const sorted = [...values].sort((a, b) => a - b);
	const mid = Math.floor(sorted.length / 2);
	return sorted.length % 2 === 0
		? (sorted[mid - 1]! + sorted[mid]!) / 2
		: sorted[mid]!;
}

function clusterWithinTolerance(quotes: PriceQuote[]): boolean {
	if (quotes.length === 0) return false;
	const prices = quotes.map((q) => q.price);
	const med = median(prices);
	const tol = toleranceUsd(med);
	return quotes.every((q) => Math.abs(q.price - med) <= tol);
}

export type ConsensusResult = {
	average: number;
	cluster: PriceQuote[];
	providerCount: number;
};

/** Largest provider cluster (≥ MIN_CONSENSUS_PROVIDERS) within tolerance band. */
export function findConsensusCluster(
	quotes: PriceQuote[],
): ConsensusResult | null {
	const valid = quotes.filter(
		(q) => Number.isFinite(q.price) && q.price > 0,
	);
	if (valid.length < MIN_CONSENSUS_PROVIDERS) return null;

	const sorted = [...valid].sort((a, b) => a.price - b.price);
	let best: PriceQuote[] = [];

	for (let i = 0; i < sorted.length; i++) {
		for (let j = sorted.length - 1; j >= i; j--) {
			const window = sorted.slice(i, j + 1);
			if (window.length < MIN_CONSENSUS_PROVIDERS) continue;
			if (!clusterWithinTolerance(window)) continue;
			if (window.length > best.length) best = window;
		}
	}

	if (best.length < MIN_CONSENSUS_PROVIDERS) return null;

	const average =
		best.reduce((sum, q) => sum + q.price, 0) / best.length;
	return { average, cluster: best, providerCount: best.length };
}

export function usdToE8(usdPrice: number): bigint {
	return BigInt(Math.round(usdPrice * 100_000_000));
}
